import { useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';

import { ParkingSessionForm } from './parking-session-form';
import {
	buildCreateParkingSessionInput,
	generateIdempotentParkingSessionId,
	type ParkingSessionFormSubmitData
} from './parking-session-input';
import { CREATE_PARKING_SESSION_MUTATION } from './parking-sessions.gql';

export const ParkingSessionsCreate = () => {
	const { t } = useTranslation();
	const go = useGo();
	const { setPageHeader, resetPageHeader } = usePageHeader();

	const idempotentParkingSessionId = useMemo(
		() => generateIdempotentParkingSessionId(),
		[]
	);

	const [createParkingSession, { loading: isSubmitting }] = useMutation(
		CREATE_PARKING_SESSION_MUTATION
	);

	useEffect(() => {
		setPageHeader({
			title: t('parkingSessions:create.title'),
			description: t('parkingSessions:create.description'),
			showBackButton: true,
			onBackClick: () => go({ to: '/parking-sessions' })
		});

		return () => {
			resetPageHeader();
		};
	}, [setPageHeader, resetPageHeader, go, t]);

	const handleSubmit = async (formData: ParkingSessionFormSubmitData) => {
		const payload = buildCreateParkingSessionInput(
			formData,
			idempotentParkingSessionId
		);

		try {
			const { data } = await createParkingSession(
				getMutationOptions('parkingSessions', { input: payload })
			);

			const message = extractCheckResultError(data?.createParkingSession);

			if (message) {
				showMutationErrorToast('parkingSessions', 'create', { message });
				return;
			}

			showMutationSuccessToast('parkingSessions', 'create');
			go({
				to: '/parking-sessions',
				state: { selectParkingSessionId: idempotentParkingSessionId }
			});
		} catch (error) {
			showMutationErrorToast('parkingSessions', 'create', { error });
		}
	};

	return (
		<FormPageLayout>
			<Card>
				<ParkingSessionForm
					mode="create"
					onSubmit={handleSubmit}
					isSubmitting={isSubmitting}
					headerTitle={t('parkingSessions:create.title')}
				/>
			</Card>
		</FormPageLayout>
	);
};
